import React from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import PatientStyle from "../../styles/Patient.module.css";
import { ModalForm, Modal, InputField } from "../../components/Modal";
import ModalHeading from "../../components/Modal/ModalHeading";
import AddUserSkeleton from "../../Skeletons/AddUserSkeleton";
import { Button } from "../../components/Modal";
import ConfirmationModal, {
  sendConfrimationModalState,
} from "./ConfirmationModal";

import EnrollPatientStyle from "../../styles/EnrollPatient.module.css";
import PersonalInfoSection from "./PersonalSection";
import ClinicInfoModal from "./ClinicInfoSection";
import InsuranceInfoSection from "./InsuranceInfo";
import ConfirmationInfo from "./ConfirmationInfo";

import { PropsWithChildren } from "react";
import {
  useFetchState,
  useFetchAssociatedClinic,
  useSendKey,
} from "../../hooks/patients";
import {
  IAddKeyReq,
  IAddPatientReq,
  IPatient,
  IPatientRes,
  IPatientResNew,
} from "../../hooks/patients/index.type";
import { loginSliceValue } from "../../redux/reducers/loginSlice";
import {
  validateEmail,
  validatePhone,
  unMasking,
  inputPhoneMasking,
} from "../../utils/validators";
import { useAddPatient } from "../../hooks/patients";
import { Socket } from "socket.io-client";
import {
  generateUserKeyPair,
  extractNumbersFromId,
} from "../../utils/generateKey";
import EnrollPatientSkeleton from "../../Skeletons/EnrollPatientSkeleton";
import { onlineUsersSliceValue } from "../../redux/reducers/onlineUsers";
import CheckboxInput from "../../components/Modal/CheckBoxInput";

export interface AddPatientModalState {
  status: boolean;
  data?: IPatient;
}

export type IntakeFormQuestion = {
  question_id?: string;
  question_answer?: string | string[] | number;
  question_type?: "single_option" | "multiple_option" | "scale" | "textarea";
};

// export type IaddPatientModalStates = PropsWithChildren<{
//   name: { value: string; error: string };
//   // dob: { value: string; error: string }
//   dobString: { value: string; error: string };
//   gender: { value: string; error: string };
//   phone: { value: string; error: string };
//   email: { value: string; error: string };
//   address1: { value: string; error: string };
//   address2: { value: string; error: string };
//   state: { value: string; error: string };
//   city: { value: string; error: string };
//   zip: { value: string; error: string };
//   associatedClinic: { value: string; error: string };
//   billingProvider: { value: string; error: string };
//   plan: { value: string; error: string };
//   intakeForm: IntakeFormQuestion[];
//   clinicianId: String;
//   isIntakeFormComplete?: boolean;
// }>;

export type IaddPatientModalStates = PropsWithChildren<{
  name: { value: string; error: string };
  dobString: { value: string; error: string };
  gender: { value: string; error: string };
  phone: { value: string; error: string };
  email: { value: string; error: string };
  address1: { value: string; error: string };
  address2: { value: string; error: string };
  state: { value: string; error: string };
  city: { value: string; error: string };
  zip: { value: string; error: string };
  associatedClinic: { value: string; error: string };
  billingProvider: { value: string; error: string };
  bestTimeToContact: { value: string; error: string };
  primaryInsurance: {
    provider: { value: string; error: string };
    policyNumber: { value: string; error: string };
    groupNumber: { value: string; error: string };
  };
  secondaryInsurance: {
    provider: { value: string; error: string };
    policyNumber: { value: string; error: string };
    groupNumber: { value: string; error: string };
  };
  rtmSelfPay: { value: "yes" | "no"; error: string };
  rtmRedFlag: { value: "yes" | "no"; error: string };
  rtmRedFlagReason: { value: string; error: string };
}>;

interface AddEditModalProps {
  loading: boolean;
  addEditModal: AddPatientModalState;
  onCloseModal: () => void;
  handleAddEdit?: (event: React.FormEvent<HTMLFormElement>) => void;
  addEditModalStates: IaddPatientModalStates;
  setAddEditModalStates: React.Dispatch<
    React.SetStateAction<IaddPatientModalStates>
  >;
  addPatientModalStates: IaddPatientModalStates;
  setAddPatientModalStates: React.Dispatch<
    React.SetStateAction<IaddPatientModalStates>
  >;
  getPatientData: () => void;
  socket: Socket | null;
  patientData?: IPatientRes;
  setPatientData?: React.Dispatch<
    React.SetStateAction<IPatientRes | undefined>
  >;
}

const EnrollPatientModal: React.FC<AddEditModalProps> = (props) => {
  // const [showConfirmationModal, setShowConfirmationModal] =
  //   React.useState<sendConfrimationModalState>({
  //     status: false,
  //   });
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [isConsentChecked, setIsConsentChecked] = React.useState(false);

  const { userDetails } = useSelector(loginSliceValue);
  const clinicId = userDetails?.data?.user?.clinic?._id || "";
  const clinicianId = userDetails?.data?.user?._id || "";
  const isUpdateMode = !!(
    props.addEditModal.data && props.addEditModal.data._id
  );
  const { stateLoading, getState, states } = useFetchState();
  const { associatedClinic, associatedClinicLoading, getAssociatedClinic } =
    useFetchAssociatedClinic({ clinicId });
  const { addPatient, addPatientLoading } = useAddPatient({
    patientId: props.addEditModal?.data?._id ?? "",
  });
  const { addKey } = useSendKey();

  const handleConsentChange = (isConsentChecked: boolean) => {
    setIsConsentChecked(isConsentChecked);
  };

  React.useEffect(() => {
    getState();
  }, []);

  React.useEffect(() => {
    if (clinicId) {
      getAssociatedClinic();
    }
  }, [clinicId]);

  // Function to check if all required fields are filled (without setting errors)
  const areRequiredFieldsFilled = () => {
    const {
      name,
      dobString,
      gender,
      phone,
      email,
      associatedClinic,
      billingProvider,
    } = props.addPatientModalStates;

    // Check basic required fields
    if (!name?.value.trim()) return false;
    if (!dobString.value.trim()) return false;
    if (!gender.value.trim()) return false;
    if (!phone.value.trim() || !validatePhone(unMasking(phone.value)))
      return false;
    if (!email.value.trim() || !validateEmail(email.value)) return false;
    if (!associatedClinic.value.trim()) return false;
    if (!billingProvider.value.trim()) return false;
    return true;
  };

  const handleValidation = (event: React.FormEvent | React.MouseEvent) => {
    event.preventDefault();

    const {
      name,
      dobString,
      gender,
      phone,
      email,
      associatedClinic,
      billingProvider,
      zip,
    } = props.addPatientModalStates;

    if (!name?.value.trim()) {
      props.setAddPatientModalStates((prev) => ({
        ...prev,
        name: { ...prev.name, error: "Please enter full name" },
      }));
      return false;
    }

    if (!dobString.value.trim()) {
      props.setAddPatientModalStates((prev) => ({
        ...prev,
        dobString: { ...prev.dobString, error: "Please enter date of birth" },
      }));
      return false;
    }

    if (!gender.value.trim()) {
      props.setAddPatientModalStates((prev) => ({
        ...prev,
        gender: { ...prev.gender, error: "Please select gender" },
      }));
      return false;
    }

    if (!phone.value.trim() || !validatePhone(unMasking(phone.value))) {
      props.setAddPatientModalStates((prev) => ({
        ...prev,
        phone: { ...prev.phone, error: "Please enter valid phone number" },
      }));
      return false;
    }

    if (!email.value.trim() || !validateEmail(email.value)) {
      props.setAddPatientModalStates((prev) => ({
        ...prev,
        email: { ...prev.email, error: "Please enter valid email address" },
      }));
      return false;
    }

    if (!associatedClinic.value.trim()) {
      props.setAddPatientModalStates((prev) => ({
        ...prev,
        associatedClinic: {
          ...prev.associatedClinic,
          error: "Please select clinic",
        },
      }));
      return false;
    }

    if (!billingProvider.value.trim()) {
      props.setAddPatientModalStates((prev) => ({
        ...prev,
        billingProvider: {
          ...prev.billingProvider,
          error: "Please select billing provider",
        },
      }));
      return false;
    }

    if (zip.value.trim()) {
      const zipRegEx = /^\d{5}(-\d{4})?$/;
      if (!zipRegEx.test(zip.value)) {
        props.setAddPatientModalStates((prev) => ({
          ...prev,
          zip: { ...prev.zip, error: "Please enter a valid ZIP code" },
        }));
        return false;
      }
    }

    // if (!isUpdateMode) {
    //   setShowConfirmationModal({ status: true });
    // }

    return true;
  };

  const handleAddPatient = async (
    event: React.FormEvent | React.MouseEvent
  ) => {
    event.preventDefault();

    const isValid = handleValidation(event);
    // if (!isValid || (!isUpdateMode && !showConfirmationModal.status)) return;
    if (!isValid) return;

    setIsUpdating(true);
    try {
      const formData: IAddPatientReq = {
        name: props.addPatientModalStates.name.value,
        dob: props.addPatientModalStates.dobString.value,
        dobString: props.addPatientModalStates.dobString.value,
        gender: props.addPatientModalStates.gender.value,
        phone: props.addPatientModalStates.phone.value.replace(/\D/g, ""),

        email: props.addPatientModalStates.email.value,
        rtmStatus: "Pending",

        address1: props.addPatientModalStates.address1.value,
        address2: props.addPatientModalStates.address2.value,
        state: props.addPatientModalStates.state.value,
        city: props.addPatientModalStates.city.value,
        zip: props.addPatientModalStates.zip.value,

        associatedClinic: props.addPatientModalStates.associatedClinic.value,
        billingProvider: props.addPatientModalStates.billingProvider.value,
        clinicianId: clinicianId,

        // New fields
        bestTimeToContact: props.addPatientModalStates.bestTimeToContact.value,

        primaryInsurance: {
          provider: props.addPatientModalStates.primaryInsurance.provider.value,
          policyNumber:
            props.addPatientModalStates.primaryInsurance.policyNumber.value,
          groupNumber:
            props.addPatientModalStates.primaryInsurance.groupNumber.value,
        },
        secondaryInsurance: {
          provider:
            props.addPatientModalStates.secondaryInsurance.provider.value,
          policyNumber:
            props.addPatientModalStates.secondaryInsurance.policyNumber.value,
          groupNumber:
            props.addPatientModalStates.secondaryInsurance.groupNumber.value,
        },
        rtmSelfPay: props.addPatientModalStates.rtmSelfPay.value,
        rtmRedFlag : props.addEditModalStates?.rtmRedFlag?.value,
        rtmRedFlagReason: props.addEditModalStates?.rtmRedFlagReason?.value || "",
      };
      

      const response = (await addPatient(formData)) as IPatientResNew;

      if (response?.status === "success" && response.data?._id) {
        if (isUpdateMode) {
          if (props.setPatientData && props.patientData && response.data) {
            const updatedPatient = response.data;
            const updatedPatients = props.patientData.data.data.map(
              (p: IPatient) =>
                p._id === updatedPatient._id ? updatedPatient : p
            );
            props.setPatientData((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                data: {
                  ...prev.data,
                  data: updatedPatients,
                },
              };
            });
          } else {
            props.getPatientData();
          }
          toast.success("Patient updated successfully");
          props.getPatientData();
        } else {
          if (response.data) {
            toast.success("Patient enrolled successfully!");
            const patientId = response.data._id;
            const password = await extractNumbersFromId(patientId);
            const key = await generateUserKeyPair(patientId, password);
            await addKey(key);
            props.getPatientData();
          }
        }
        props.onCloseModal();
      }
    } catch (error) {
      console.error("Failed to update patient:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Modal
        show={props.addEditModal.status}
        modalClassName={EnrollPatientStyle.customEnrollModal}
      >
        <ModalHeading
          heading={
            props.addEditModal.data && props.addEditModal.data._id
              ? "Update Patient"
              : "Enroll Patient"
          }
          customClass={EnrollPatientStyle.enrollModalHeader}
          customCloseClass={PatientStyle.close_btn}
          closeIcon="close-cross"
          onClose={props.onCloseModal}
        />
        {props.loading ? (
          <EnrollPatientSkeleton />
        ) : (
          <form
            // onSubmit={handleValidation}
            // customClass={EnrollPatientStyle.gridContainer}
            // id="modal-form-id"
            className={EnrollPatientStyle.gridContainer}
          >
            <div className={EnrollPatientStyle.flexContainer}>
              <div className={EnrollPatientStyle.personal}>
                <div className={PatientStyle.enroll_ModalForm_inner}>
                  <PersonalInfoSection
                    setAddEditModalStates={props.setAddEditModalStates}
                    addEditModalStates={props.addEditModalStates}
                    isUpdateMode={isUpdateMode}
                    states={states}
                    stateLoading={stateLoading}
                  />
                </div>
              </div>
              <div
                className={`${EnrollPatientStyle.clinic} ${EnrollPatientStyle.form_grid_section}`}
              >
                <ClinicInfoModal
                  associatedClinic={associatedClinic}
                  associatedClinicLoading={associatedClinicLoading}
                  setAddEditModalStates={props.setAddEditModalStates}
                  addEditModalStates={props.addEditModalStates}
                  isUpdateMode={isUpdateMode}
                />
              </div>
            </div>
            <div className={EnrollPatientStyle.flexContainer}>
              <div className={`${EnrollPatientStyle.insurance}`}>
                <InsuranceInfoSection
                  setAddEditModalStates={props.setAddEditModalStates}
                  addEditModalStates={props.addEditModalStates}
                  isUpdateMode={isUpdateMode}
                />
              </div>
              {!isUpdateMode && (
                <div className={EnrollPatientStyle.confirmation}>
                  <ConfirmationInfo
                    addEditModalStates={props.addPatientModalStates}
                    setAddEditModalStates={props.setAddPatientModalStates}
                    handleAddEdit={handleAddPatient}
                    socket={props.socket}
                    isFormValid={areRequiredFieldsFilled()}
                    onConsentChange={handleConsentChange}
                  />
                </div>
              )}
            
              <Button
                icon={isUpdateMode ? "icon-edit-3" : "icon-enroll-patient"}
                label={isUpdateMode ? "Update Patient" : "Enroll Patient"}
                disabled={
                  addPatientLoading ||
                  isUpdating ||
                  (!isUpdateMode && !isConsentChecked)
                }
                span={false}
                submitButtonStyle={{
                  fontSize: "1.6rem",
                  fontWeight: 400,
                }}
                type="submit"
                customClass={EnrollPatientStyle.enrollPatientButton}
                onClick={handleAddPatient}
              />
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};

export default EnrollPatientModal;
